import { observable, computed, action } from "mobx"
import { get } from "lodash"
import moment from "moment"
import * as API from "../../app/api"
import { authStore, teamStore } from "../../app/stores"
import { CHARGE_ENDPOINT } from "../../app/constants"
import { FREE_PLAN } from "./subscription-plans"
import { createToken, getPlanById, getPlanDescription } from "./utils"

class BillingStore {
  @observable error = null
  @observable saving = false
  @observable confirm = null
  @observable stripeData = null
  @observable subscription = null
  @observable showAnnual = true
  @observable selectingPlan = null

  // active subscription, may or may not be paying (non-profits)
  @computed
  get subscribed() {
    return get(this.subscription, "active", false)
  }
  // cancelled subscription but billing cycle hasn't finished (so retains access)
  @computed
  get cancelled() {
    const planEnds = get(this.subscription, "planEnds")
    return !this.subscribed && planEnds && moment().isBefore(planEnds * 1000)
  }
  // payment associated with active subscription.
  @computed
  get payment() {
    return get(this.stripeData, "customer.sources.data[0]")
  }
  @computed
  get subscriptionData() {
    return get(this.stripeData, "subscription")
  }
  // user is subscribed to this plan
  @computed
  get subscriptionPlan() {
    if (this.subscribed) {
      return getPlanById(this.subscription.plan)
    }
    return getPlanById(FREE_PLAN.id)
  }
  // user has access to this plan (includes cancelled if billing cycle hasn't finished)
  @computed
  get activePlan() {
    if (this.cancelled) {
      return getPlanById(this.subscription.plan)
    }
    return this.subscriptionPlan
  }

  @computed
  get hasAutoMessagesAccess() {
    return this.activePlan.automessages
  }
  @computed
  get hasContactsAccess() {
    return this.activePlan.contacts
  }
  @computed
  get hasWhiteLabelAccess() {
    return this.activePlan.whitelabel
  }
  @computed
  get hasMultiChannelAccess() {
    return this.activePlan.channels > 1
  }

  @action.bound
  setStripeData(stripeData) {
    this.stripeData = stripeData
    const planId = get(this.stripeData, "subscription.plan.id")
    if (planId && getPlanById(planId).interval !== 12) {
      this.showAnnual = false
    }
  }

  @action.bound
  setSubscription(subscription) {
    this.subscription = subscription
  }

  @action.bound
  setAnnual(showAnnual) {
    this.showAnnual = showAnnual
    this.selectingPlan = null
  }

  @action.bound
  selectPlan(planId) {
    const plan = getPlanById(planId)
    this.selectingPlan = plan
  }

  @action.bound
  confirmCancelSubscription() {
    this.confirm = {
      message: "Are you sure you want to cancel your subscription?",
      onConfirm: action(() => {
        this.confirm = null
        this.cancelSubscription()
      }),
    }
  }

  @action.bound
  confirmUpdateSubscription() {
    this.confirm = {
      message: `Are you sure you want to switch to ${getPlanDescription(
        this.selectingPlan
      )}?`,
      onConfirm: action(() => {
        this.confirm = null
        this.updateSubscription()
      }),
    }
  }

  @action.bound
  cancelConfirm() {
    this.confirm = null
  }

  @action.bound
  cancelSubscription() {
    this.error = null
    this.saving = true
    API.post(CHARGE_ENDPOINT, {
      type: "cancelPlan",
      subscriptionId: this.subscriptionData.id,
      slackTeamId: teamStore.teamId,
      uid: authStore.currentUser.uid,
    })
      .catch(action(err => (this.error = err)))
      .then(action(() => (this.saving = false)))
  }

  @action.bound
  createSubscription(form, email) {
    if (this.saving) return
    this.error = null
    this.saving = true
    createToken(form)
      .then(token => {
        return API.post(CHARGE_ENDPOINT, {
          type: "createPlan",
          token,
          email,
          uid: authStore.currentUser.uid,
          newPlanId: this.selectingPlan.id,
          slackTeamId: teamStore.teamId,
          customerId: get(this.subscriptionData, "customer"),
        })
      })
      .catch(action(err => (this.error = err)))
      .then(action(() => (this.saving = false)))
  }

  @action.bound
  updateSubscription() {
    this.saving = true
    return API.post(CHARGE_ENDPOINT, {
      type: "updatePlan",
      subscriptionId: this.subscriptionData.id,
      slackTeamId: teamStore.teamId,
      newPlanId: this.selectingPlan.id,
      uid: authStore.currentUser.uid,
    })
      .catch(action(err => (this.error = err)))
      .then(action(() => (this.saving = false)))
  }

  @action.bound
  updateCard(form) {
    if (this.saving) return
    this.error = null
    this.saving = true
    return new Promise(resolve => {
      createToken(form)
        .then(token => {
          return API.post(CHARGE_ENDPOINT, {
            token,
            type: "updateCard",
            slackTeamId: teamStore.teamId,
            customerId: this.subscriptionData.customer,
          })
        })
        .then(resolve)
        .catch(action(err => (this.error = err)))
        .then(action(() => (this.saving = false)))
    })
  }
}

const billingStore = new BillingStore()

export default billingStore



// WEBPACK FOOTER //
// ./src/account/billing/billing-store.js